import CustomSkeleton, {CARD_PRESET} from 'common/components/CustomSkeleton'
import DataUploadReport from 'common/components/dataUpload/DataUploadReport'
import {dispatchApi, isChunkedUploadAvailable, withUploadedFile} from 'common/helpers/chunkedUpload'
import {
  ImportReadiness,
  extractImportReadiness,
  isInstituteExcelUploadResponse,
} from 'common/helpers/csvImportReadiness'
import {
  type DataUploadConversion,
  dataUploadErrorCode,
  dataUploadErrorConversion,
  dataUploadErrorMessage,
  dataUploadErrorStatus,
  dataUploadFileExtension,
  isSpreadsheetUpload,
  normalizeDataUploadConversion,
} from 'common/helpers/dataUpload'
import {ArrowLeft, ArrowRight, Upload} from 'lucide-react'
import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'
import {
  bulkCsvImport,
  confirmCsvImport,
  getCsvImportTableSchemas,
  uploadCsvImportFile,
} from 'setup/redux/Actions/CsvImport/csvImport'

import CsvColumnMapper from '../../../DataImport/CsvImport/components/CsvColumnMapper'
import CsvImportReview from '../../../DataImport/CsvImport/components/CsvImportReview'
import ImportReadinessPanel from '../../../DataImport/CsvImport/components/ImportReadinessPanel'
import ImportResultModal from '../../../DataImport/CsvImport/components/ImportResultModal'

// Mirrors the Excel Import module's own mapping restriction list (CsvImporter.tsx).
const RESTRICTED_TABLES = [
  'timetable_preferences',
  'student_subject',
  'subject',
  'user_teacher',
  'user_student',
  'class_room',
  'class',
  'sections',
  'timetable_subcategories',
  'timetable_categories',
]

interface TableMappings {
  [table: string]: {[key: string]: string}
}

interface PreviewData {
  [key: string]: string
}

interface Props {
  show: boolean
  /** The Excel/CSV file picked on the Lesson Planning tab; upload starts when it arrives. */
  file: File | null
  isDark: boolean
  onClose: () => void
  /** Fired after a successful import once the result modal is dismissed, so the wizard can refresh its grids. */
  onImported: () => void
  /** Shown above the flow, e.g. when the file is the Teachers step's prefill, not a pick. */
  notice?: string
}

type Phase = 'uploading' | 'sheets' | 'map' | 'review' | 'failed'

/**
 * The Excel Import module's upload -> map columns -> review -> import flow, embedded in a
 * wizard-themed modal for the onboarding Lesson Planning step. Hits the exact same APIs as
 * the module (csv-import/upload, csv-import/confirm, csv-import/bulk); only the chrome
 * (routes, stats, walkthrough) is left behind. See CsvImporter.tsx for the original.
 *
 * On the AI servers the file may also be a PDF, Word file, photo or scan: `csv-import/upload`
 * (with `source=onboarding`) converts it into the Teacher Allocation template first, then runs
 * the normal flow. The response is today's response plus a `conversion` field, shown here as a
 * report. Only existing class-subject slots are filled, only with teachers who already exist;
 * rows naming anything else are listed as left out, never created.
 */
// Injected server-side by excelBulkImportBuilder when the workbook names no level. Not a
// level the user ever typed, so it never counts as content.
const FABRICATED_LEVEL_NAME = 'Default Level'

/**
 * True when the server-built bulk payload carries nothing worth importing.
 *
 * An EMPTY Teacher Allocation template still comes back as a "successful" upload: the
 * onboarding upload path skips the completeness gate (`canProceed` is forced true) and the
 * payload builder fabricates a `Default Level` branch entry whenever the file names no
 * level. Posting that payload to bulk-import creates a real "Default Level" in the tenant,
 * which then surfaces on the Curriculum/Levels screens after the post-import re-hydration.
 * Blocking it here keeps the empty payload from ever reaching the API. The backend-side
 * fix is specced in docs/backend-specs/onboarding-empty-teacher-allocation-default-level.md.
 */
const isEmptyBulkImportPayload = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return true
  const p = payload as Record<string, unknown>
  const rows = (key: string) => (Array.isArray(p[key]) ? (p[key] as unknown[]).length : 0)
  if (rows('teachers') + rows('classes') + rows('subjects') + rows('students') > 0) return false
  const branches = Array.isArray(p.branch) ? (p.branch as Array<Record<string, unknown>>) : []
  const hasRealLevel = branches.some((b) =>
    (Array.isArray(b?.levels) ? (b.levels as Array<Record<string, unknown>>) : []).some(
      (lvl) => String(lvl?.name ?? '').trim() !== FABRICATED_LEVEL_NAME
    )
  )
  return !hasRealLevel
}

const OnboardingExcelImportModal: FC<Props> = ({
  show,
  file,
  isDark,
  onClose,
  onImported,
  notice,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const [phase, setPhase] = useState<Phase>('uploading')
  const [importing, setImporting] = useState(false)

  // Upload-response state (same shape the Excel Import module keeps).
  const [preview, setPreview] = useState<PreviewData[]>([])
  const [tableMappings, setTableMappings] = useState<TableMappings>({})
  const [columnMatches, setColumnMatches] = useState<{[key: string]: any}>({})
  const [csvColumns, setCsvColumns] = useState<string[]>([])
  const [columnMappings, setColumnMappings] = useState<{[csvColumn: string]: string}>({})
  const [warnings, setWarnings] = useState<string[]>([])
  const [foreignKeyOptions, setForeignKeyOptions] = useState<{[key: string]: any[]}>({})
  const [spreadsheetTabs, setSpreadsheetTabs] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState<string | null>(null)
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [schemas, setSchemas] = useState<{[table: string]: string[]}>({})
  const [defaultValues, setDefaultValues] = useState<{[key: string]: any}>({})
  const [hasUnresolvedColumns, setHasUnresolvedColumns] = useState(false)

  // Institute-template flow: the backend maps the template server-side and returns a
  // ready-to-send bulk payload + readiness verdict instead of per-column matches.
  const [isInstituteExcelFlow, setIsInstituteExcelFlow] = useState(false)
  const [importReadiness, setImportReadiness] = useState<ImportReadiness | null>(null)
  const [bulkImportPayload, setBulkImportPayload] = useState<any>(null)

  const [importResult, setImportResult] = useState<any>(null)
  const [showResultModal, setShowResultModal] = useState(false)

  /** Set when the server converted the file first (`result.conversion`). */
  const [conversion, setConversion] = useState<DataUploadConversion | null>(null)
  /**
   * A conversion that produced no usable rows (422 NO_ROWS_CONVERTED): the modal stays open
   * on its report, which says why each row was left out, instead of closing on a toast.
   */
  const [conversionError, setConversionError] = useState<string | null>(null)
  /** 0..1 while the file's pieces are being sent (AI servers); null otherwise. */
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const schemasFetchedRef = useRef(false)

  const textColor = isDark ? '#ffffff' : '#181C32'
  const mutedColor = isDark ? '#92929f' : '#7E8299'

  const resetState = useCallback(() => {
    setPhase('uploading')
    setImporting(false)
    setPreview([])
    setTableMappings({})
    setColumnMatches({})
    setCsvColumns([])
    setColumnMappings({})
    setWarnings([])
    setForeignKeyOptions({})
    setSpreadsheetTabs([])
    setSelectedTab(null)
    setSelectedTable(null)
    setDefaultValues({})
    setHasUnresolvedColumns(false)
    setIsInstituteExcelFlow(false)
    setImportReadiness(null)
    setBulkImportPayload(null)
    setConversion(null)
    setConversionError(null)
    setUploadProgress(null)
  }, [])

  // Same rename rule as the Excel Import module: with sections but no grades, the
  // backend's `class` mapping actually targets grades.
  const transformTableMappings = (mappings: TableMappings): TableMappings => {
    if (!mappings) return {}
    if (mappings.sections && mappings.class && !mappings.grades) {
      const {class: classMapping, ...rest} = mappings
      return {...rest, grades: classMapping}
    }
    return mappings
  }

  const uploadFile = useCallback(
    (selectedFile: File, sheet?: string) => {
      setPhase('uploading')
      /** The file itself, or (sent in pieces first) its `uploadId`. */
      const request = (uploadId: string | null) => {
        const formData = new FormData()
        if (uploadId) formData.append('uploadId', uploadId)
        else formData.append('file', selectedFile)
        if (sheet) formData.append('sheet', sheet)
        // Onboarding uploads skip the strict completeness validation server-side;
        // the sidebar Excel Import module (no source flag) keeps it.
        formData.append('source', 'onboarding')
        return dispatchApi(dispatch, (ok, fail) => uploadCsvImportFile(formData, ok, fail))
      }
      // nginx refuses bodies over 1 MB on the AI servers: the file goes in 512 KB pieces there
      // (a picked sheet again reuses the same upload). Legacy .xls isn't a data-upload format.
      const chunked =
        isChunkedUploadAvailable() && dataUploadFileExtension(selectedFile.name) !== 'xls'
      const pending = chunked
        ? withUploadedFile(
            dispatch,
            selectedFile,
            {useCaseKey: 'allocation', onProgress: setUploadProgress},
            (uploadId) => {
              setUploadProgress(null)
              return request(uploadId)
            }
          )
        : request(null)

      pending.then(
        (response: any) => {
          setUploadProgress(null)
          const responseData = response.result || response.data || response
          if (!(response.statusCode === 200 || response.status === 'true')) {
            toast.error(
              response.result?.error ||
                response.message ||
                intl.formatMessage({id: 'CSV_IMPORT.ERROR.UPLOAD_FAILED'})
            )
            onClose()
            return
          }

          const {
            preview: previewData,
            mappings,
            columnMatches: matches,
            sheets,
            warnings: responseWarnings,
            foreignKeyOptions: fkOptions,
          } = responseData

          setConversion(normalizeDataUploadConversion(responseData?.conversion))

          const instituteFlow = isInstituteExcelUploadResponse(responseData)
          setIsInstituteExcelFlow(instituteFlow)
          const serverPayload =
            responseData?.bulkImportPayload ?? responseData?.result?.bulkImportPayload ?? null
          let readiness = extractImportReadiness(responseData)
          let payloadToImport = serverPayload
          // Empty template: block instead of trusting the server's forced `canProceed`.
          if (instituteFlow && isEmptyBulkImportPayload(serverPayload)) {
            readiness = {
              ...(readiness ?? {}),
              canProceed: false,
              summaryMessage: intl.formatMessage({id: 'CSV_IMPORT.ERROR.EMPTY_FILE'}),
              missingItems: [],
              warningMessage: undefined,
              warnings: [],
            }
            // Dropped, not kept: handleImport must have nothing to send even if a later
            // state change were to flip `blocked` off.
            payloadToImport = null
          }
          setImportReadiness(readiness)
          setBulkImportPayload(payloadToImport)
          setPreview(previewData || [])
          setWarnings(responseWarnings || [])
          setForeignKeyOptions(fkOptions || {})

          if (instituteFlow) {
            // Blocked or auto-mapped: either way the map phase renders the right panel.
            const hasMappingData =
              (mappings && Object.keys(mappings).length > 0) ||
              (matches && Object.keys(matches).length > 0)
            if (!hasMappingData || readiness?.canProceed === false) {
              setPhase('map')
              return
            }
          }

          if (!instituteFlow && sheets && sheets.length > 0) {
            setSpreadsheetTabs(sheets)
            setPhase('sheets')
            return
          }

          const transformedMappings = transformTableMappings(mappings)
          setTableMappings(transformedMappings)
          setColumnMatches(matches || {})
          if (transformedMappings && Object.keys(transformedMappings).length > 0) {
            setSelectedTable(Object.keys(transformedMappings)[0])
          }

          if (!instituteFlow && (!matches || Object.keys(matches).length === 0)) {
            toast.error(intl.formatMessage({id: 'CSV_IMPORT.ERROR.NO_COLUMN_MATCHES'}))
            onClose()
            return
          }

          // Initialize mappings from the matches, enforcing one-to-one like the module
          // (all user_teacher columns are exempt and may repeat).
          const columns = matches ? Object.keys(matches) : []
          setCsvColumns(columns)
          const initialMappings: {[key: string]: string} = {}
          const usedDbColumns = new Set<string>()
          const isUserTeacherColumn = (dbColumnName: string) =>
            !!dbColumnName && dbColumnName.includes('user_teacher')
          const sortedColumns = [...columns].sort(
            (a, b) => (matches?.[b]?.similarity ?? 0) - (matches?.[a]?.similarity ?? 0)
          )
          sortedColumns.forEach((csvCol) => {
            const match = matches?.[csvCol]
            let dbColumnName: string | null = null
            if (match) {
              const column =
                match.dbColumn || match.column || match.databaseColumn || match.targetColumn
              if (column && match.table) dbColumnName = `${match.table}.${column}`
            }
            if (
              dbColumnName &&
              (!usedDbColumns.has(dbColumnName) || isUserTeacherColumn(dbColumnName))
            ) {
              initialMappings[csvCol] = dbColumnName
              if (!isUserTeacherColumn(dbColumnName)) usedDbColumns.add(dbColumnName)
            } else {
              initialMappings[csvCol] = ''
            }
          })
          setColumnMappings(initialMappings)
          setPhase('map')
        },
        (error: any) => {
          setUploadProgress(null)
          // A converted upload reports its failure as `result.code` (+ `result.conversion`
          // when rows were read but none matched the school). A chunked upload fails with
          // the same codes, or with no answer at all (a proxy page, a dropped connection).
          const code = dataUploadErrorCode(error)
          const converted = !isSpreadsheetUpload(selectedFile)
          const noAnswer =
            error instanceof Error ||
            [408, 413, 502, 504].includes(dataUploadErrorStatus(error) ?? 0)
          if (code || converted || (chunked && noAnswer)) {
            const message = dataUploadErrorMessage(intl.formatMessage, error, {
              useCaseKey: 'allocation',
              fileName: selectedFile.name,
            })
            const failedConversion = dataUploadErrorConversion(error)
            if (failedConversion) {
              setConversion(failedConversion)
              setConversionError(message)
              setPhase('failed')
              return
            }
            toast.error(message, {autoClose: 10000})
            onClose()
            return
          }
          const errData = error?.response?.data || error?.result || error
          toast.error(
            errData?.result?.error ||
              errData?.error ||
              errData?.message ||
              error?.message ||
              intl.formatMessage({id: 'CSV_IMPORT.ERROR.UPLOAD_FAILED'})
          )
          onClose()
        }
      )
    },
    [dispatch, intl, onClose]
  )

  // Kick the upload off whenever the modal opens with a fresh file.
  useEffect(() => {
    if (!show || !file) return
    resetState()
    uploadFile(file)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, file])

  // Table schemas feed the mapper's dropdowns; fetched once, filtered like the module.
  useEffect(() => {
    if (!show || schemasFetchedRef.current) return
    schemasFetchedRef.current = true
    dispatch(
      getCsvImportTableSchemas(
        (res: any) => {
          const allSchemas = res?.result?.schemas || res?.result || {}
          const filtered: {[table: string]: string[]} = {}
          Object.entries(allSchemas).forEach(([table, columns]) => {
            if (RESTRICTED_TABLES.includes(table)) {
              filtered[table] = columns as string[]
            }
          })
          setSchemas(filtered)
        },
        () => {}
      ) as never
    )
  }, [show, dispatch])

  const finishAfterResult = () => {
    setShowResultModal(false)
    setImportResult(null)
    resetState()
    onImported()
    onClose()
  }

  const handleImportSuccess = (response: any) => {
    const responseData = response.result || response.data || response
    const isSuccessResponse =
      response?.statusCode === 200 ||
      response?.status === 'true' ||
      response?.status === true ||
      response?.success === 'true' ||
      response?.success === true
    setImporting(false)
    if (!isSuccessResponse) {
      toast.error(response.message || intl.formatMessage({id: 'CSV_IMPORT.ERROR.IMPORT_FAILED'}))
      return
    }
    toast.success('Imported Successfully!')
    setImportResult({...responseData, message: response.message || responseData.message || ''})
    setShowResultModal(true)
  }

  const handleImportFailure = (error: any) => {
    setImporting(false)
    const responseData = error?.result || error?.data || error
    const readiness = extractImportReadiness(responseData) ?? extractImportReadiness(error)
    if (readiness) {
      setImportReadiness(readiness)
      if (readiness.canProceed === false) setPhase('map')
    }
    toast.error(
      readiness?.summaryMessage ||
        error?.message ||
        intl.formatMessage({id: 'CSV_IMPORT.ERROR.IMPORT_FAILED'})
    )
  }

  const handleImport = () => {
    if (!file) return

    if (isInstituteExcelFlow) {
      // Second line of defence for the empty-template case (see isEmptyBulkImportPayload).
      if (isEmptyBulkImportPayload(bulkImportPayload)) {
        toast.error(intl.formatMessage({id: 'CSV_IMPORT.ERROR.EMPTY_FILE'}))
        return
      }
      if (importReadiness?.canProceed === false) {
        toast.error(
          importReadiness.summaryMessage ||
            'Please complete all required data in the Excel file before continuing.'
        )
        return
      }
      setImporting(true)
      dispatch(
        bulkCsvImport(
          // source: 'onboarding' with no checkpoint marks this as the onboarding Excel import,
          // so node-api drops a "Default Level" nothing in the payload uses (backend spec
          // onboarding-empty-teacher-allocation-default-level.md, part C). Do not add checkpoint.
          {...(bulkImportPayload as Record<string, unknown>), source: 'onboarding'},
          handleImportSuccess,
          handleImportFailure
        ) as never
      )
      return
    }

    setImporting(true)

    // Rebuild tableMappings from the current columnMappings so manual changes made in
    // the mapper are reflected in the payload — same construction as the module.
    const finalTableMappings: TableMappings = {}
    Object.entries(columnMappings).forEach(([csvColumn, mapping]) => {
      if (!mapping) return
      const parts = mapping.split('.').filter(Boolean)
      if (parts.length < 2) return
      const dbColumn = parts[parts.length - 1]
      const tableName = parts[parts.length - 2]
      if (!finalTableMappings[tableName]) finalTableMappings[tableName] = {}
      finalTableMappings[tableName][csvColumn] = dbColumn
    })

    if (defaultValues && Object.keys(defaultValues).length > 0) {
      Object.entries(defaultValues).forEach(([column, option]: [string, any]) => {
        let mappingKey: string
        let databaseColumn: string
        let tableName: string
        if (column === 'user_teacher') {
          mappingKey = 'teacher_cnic'
          databaseColumn = 'cnic'
          tableName = 'user_teacher'
        } else if (column === 'grades') {
          mappingKey = 'grades_name'
          databaseColumn = 'name'
          tableName = 'grades'
        } else {
          mappingKey = `${column}_name`
          databaseColumn = 'name'
          tableName = column
        }
        if (!finalTableMappings[tableName]) finalTableMappings[tableName] = {}
        finalTableMappings[tableName][mappingKey] = databaseColumn
        const selectedValue = column === 'user_teacher' ? option.cnic : option.name
        preview.forEach((row) => {
          row[mappingKey] = selectedValue
        })
      })
    }

    const isExcelFile =
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls') ||
      spreadsheetTabs.length > 0 ||
      !!selectedTab

    dispatch(
      confirmCsvImport(
        {
          data: preview,
          tableMappings: finalTableMappings,
          columnMappings,
          fileType: isExcelFile ? 'spreadsheet' : 'csv',
          dataType: 'all',
          targetTable: selectedTable,
          sheet: selectedTab,
          studentSubject: 'manual',
        },
        handleImportSuccess,
        handleImportFailure
      ) as never
    )
  }

  const blocked = importReadiness?.canProceed === false
  const showReadinessPanel =
    blocked ||
    Boolean(importReadiness?.warningMessage) ||
    (importReadiness?.warnings?.length ?? 0) > 0
  const autoMapped =
    isInstituteExcelFlow && csvColumns.length === 0 && Boolean(bulkImportPayload) && !blocked

  const isNextDisabled =
    phase !== 'map' || blocked || (!autoMapped && hasUnresolvedColumns) || importing
  const isImportDisabled =
    importing ||
    blocked ||
    (!isInstituteExcelFlow &&
      Object.values(columnMappings).filter((mapping) => mapping !== '').length === 0) ||
    (isInstituteExcelFlow && !bulkImportPayload)

  /** Section -> row-count summary for a template the backend already auto-mapped. */
  const renderAutoMappedSummary = () => (
    <div
      style={{
        border: `1px solid ${isDark ? '#2b2b40' : '#e1e3ea'}`,
        borderRadius: 12,
        padding: '1.25rem 1.5rem',
      }}
    >
      <p className='mb-3' style={{color: textColor, fontWeight: 600}}>
        {intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})}
      </p>
      <div className='d-flex flex-wrap gap-2'>
        {Object.entries(bulkImportPayload || {})
          .filter(([, value]) => Array.isArray(value))
          .map(([section, value]) => (
            <span
              key={section}
              className='badge badge-light-primary'
              style={{fontSize: '0.85rem', padding: '0.55rem 0.8rem'}}
            >
              {section}: {(value as any[]).length}
            </span>
          ))}
      </div>
      <p className='mt-3 mb-0' style={{color: mutedColor, fontSize: '0.9rem'}}>
        {intl.formatMessage({id: 'CSV_IMPORT.INSTRUCTIONS.DOWNLOAD_TEMPLATE_ABOVE'})}
      </p>
    </div>
  )

  return (
    <>
      {/* No bg-dark here: Metronic's dark theme INVERTS the `dark` semantic color
          ($dark-dark: #f9f9f9), so bg-dark painted the modal light exactly in dark
          mode. The default modal bg is var(--bs-body-bg), themed correctly. */}
      <Modal
        show={show && !showResultModal}
        onHide={importing ? () => {} : onClose}
        size='xl'
        centered
        backdrop='static'
      >
        <Modal.Header closeButton={!importing}>
          <Modal.Title style={{color: textColor}}>
            {intl.formatMessage({id: 'CSV_IMPORT.UPLOAD.TITLE'})}
            <span
              className='ms-3'
              style={{color: mutedColor, fontSize: '0.85rem', fontWeight: 500}}
            >
              {phase === 'review'
                ? intl.formatMessage({id: 'CSV_IMPORT.STEPS.REVIEW_IMPORT'})
                : intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {notice && (
            <div className='alert alert-primary d-flex align-items-start gap-2 mb-4' role='status'>
              {notice}
            </div>
          )}
          {phase === 'uploading' && (
            <>
              {uploadProgress !== null && (
                <div className='text-center mb-4' role='status' aria-live='polite'>
                  <strong style={{color: textColor}}>
                    {intl.formatMessage(
                      {id: 'DATA_UPLOAD.CONVERT.UPLOADING'},
                      {percent: Math.round(uploadProgress * 100)}
                    )}
                  </strong>
                  <div
                    className='progress mx-auto mt-2'
                    style={{height: 6, maxWidth: 320}}
                    role='progressbar'
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(uploadProgress * 100)}
                  >
                    <div
                      className='progress-bar'
                      style={{width: `${Math.round(uploadProgress * 100)}%`}}
                    />
                  </div>
                </div>
              )}
              {/* A PDF, Word file or photo is read server-side: up to a minute per page. */}
              {uploadProgress === null && file && !isSpreadsheetUpload(file) && (
                <div className='text-center mb-4' role='status' aria-live='polite'>
                  <div className='d-inline-flex align-items-center gap-2'>
                    <span className='spinner-border spinner-border-sm text-primary' aria-hidden />
                    <strong style={{color: textColor}}>
                      {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE'})}
                    </strong>
                  </div>
                  <div className='mt-1' style={{color: mutedColor, fontSize: '0.88rem'}}>
                    {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})}
                  </div>
                </div>
              )}
              <CustomSkeleton variant={CARD_PRESET.TABLE} />
            </>
          )}

          {phase === 'failed' && conversionError && (
            <div className='alert alert-danger mb-4' role='alert'>
              {conversionError}
            </div>
          )}

          {conversion && phase !== 'uploading' && (
            <DataUploadReport conversion={conversion} isDark={isDark} className='mb-4' />
          )}

          {phase === 'sheets' && (
            <div className='d-flex flex-column gap-2'>
              <p style={{color: textColor, fontWeight: 600}}>
                {intl.formatMessage({id: 'CSV_IMPORT.STEPS.UPLOAD_FILE'})}
              </p>
              {spreadsheetTabs.map((tab) => (
                <button
                  key={tab}
                  type='button'
                  className='btn btn-light-primary text-start'
                  onClick={() => {
                    setSelectedTab(tab)
                    if (file) uploadFile(file, tab)
                  }}
                >
                  <Upload size={14} className='me-2' aria-hidden />
                  {tab}
                </button>
              ))}
            </div>
          )}

          {(phase === 'map' || phase === 'review') && showReadinessPanel && (
            <ImportReadinessPanel importReadiness={importReadiness} className='mb-4' />
          )}

          {phase === 'map' &&
            !blocked &&
            (autoMapped ? (
              renderAutoMappedSummary()
            ) : (
              <CsvColumnMapper
                csvColumns={csvColumns}
                columnMappings={columnMappings}
                columnMatches={columnMatches}
                schemas={schemas}
                selectedTable={selectedTable}
                tableMappings={tableMappings}
                warnings={warnings}
                foreignKeyOptions={foreignKeyOptions}
                onColumnMappingChange={(csvColumn, dbColumn) =>
                  setColumnMappings((prev) => ({...prev, [csvColumn]: dbColumn}))
                }
                onDefaultValuesChange={setDefaultValues}
                onHasUnresolvedColumns={setHasUnresolvedColumns}
              />
            ))}

          {phase === 'review' && (
            <CsvImportReview
              file={file}
              preview={preview}
              tableMappings={tableMappings}
              columnMappings={columnMappings}
              warnings={warnings}
              foreignKeyOptions={foreignKeyOptions}
              defaultValues={defaultValues}
              onDefaultValuesChange={setDefaultValues}
              // The allocation rows bulk-import actually receives, not the file's preview rows.
              recordCount={
                Array.isArray(bulkImportPayload?.subjects)
                  ? bulkImportPayload.subjects.length
                  : undefined
              }
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          {phase === 'failed' && (
            <button type='button' className='onboarding-btn-next' onClick={onClose}>
              {intl.formatMessage({id: 'GENERAL.CLOSE'})}
            </button>
          )}
          {phase === 'review' && (
            <button
              type='button'
              className='onboarding-btn-back me-auto'
              disabled={importing}
              onClick={() => setPhase('map')}
            >
              <ArrowLeft size={16} className='me-2' />
              {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.BACK'})}
            </button>
          )}
          {phase === 'map' && (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={isNextDisabled}
              onClick={() => setPhase('review')}
            >
              {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.NEXT'})}
              <ArrowRight size={16} className='ms-2' />
            </button>
          )}
          {phase === 'review' && (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={isImportDisabled}
              onClick={handleImport}
            >
              {importing ? (
                <span className='spinner-border spinner-border-sm me-2' role='status' />
              ) : null}
              {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.IMPORT_DATA'})}
            </button>
          )}
        </Modal.Footer>
      </Modal>

      <ImportResultModal
        show={showResultModal}
        onHide={finishAfterResult}
        result={importResult}
        onClose={finishAfterResult}
        disableDoneNavigation
      />
    </>
  )
}

export default OnboardingExcelImportModal
